import dateFormat from 'dateformat';
import { Box, Button, Flex, Header, Loader, Message, Modal, Scroll } from 'components';
import { CommonCard, ForeignPassportCard, MessagePoint, ModalGetVisa, PassportCard, VisaList } from './components';
import { useEffect, useState } from 'react';
import { ICommonInput, IForeignPassportInput, IPassportInput, IVisaInput } from './types';
import './style.scss';
import { ApiPassengers } from 'common/api';
import {
  IClientPassengerFull,
  IClientPassengerPostPutForeignPassport,
  IClientPassengerPostPutPassport,
} from '@bustickus/models-client';

type Stage = 'common' | 'loading' | 'success' | 'error';
type OpenedTab = 'common' | 'passport' | 'foreign-passport' | 'visa';

interface IProps {
  passengerId?: string | null;
  onClose: () => void;
  onSaved: () => void;
}

function ModalAddPassenger(props: IProps) {
  const [stage, setStage] = useState<Stage>(props.passengerId != null ? 'loading' : 'common');
  const [passenger, setPassenger] = useState<IClientPassengerFull | null>(null);
  const [tab, setTab] = useState<OpenedTab | null>('common');
  const [visaId, setVisaId] = useState<string | null>(null);
  const [common, setCommon] = useState<ICommonInput | null>(null);
  const [passport, setPassport] = useState<IPassportInput | null>(null);
  const [foreignPassport, setForeignPassport] = useState<IForeignPassportInput | null>(null);
  const [visas, setVisas] = useState<(IVisaInput | null)[]>([]);
  const [getVisaModal, setGetVisaModal] = useState<boolean>(false);

  useEffect(() => {
    loadPassenger();
  }, []);

  const handleTabOpen = (senderTab: OpenedTab, senderVisaCountryId?: string | null) => {
    if (senderTab === 'visa') {
      if (tab === 'visa' && visaId === senderVisaCountryId) {
        setVisaId(null);
        setTab(null);
      } else {
        setVisaId(senderVisaCountryId ?? null);
        setTab('visa');
      }
    } else {
      if (tab === senderTab) {
        setTab(null);
      } else {
        setTab(senderTab);
      }
    }
  };

  const loadPassenger = async () => {
    if (props.passengerId != null) {
      try {
        const apiPassengers = ApiPassengers.getInstance();
        const passenger = await apiPassengers.getById(props.passengerId);
        setPassenger(passenger);
        setStage('common');
      } catch {
        setStage('error');
      }
    }
  };

  const handleSaveClick = async () => {
    try {
      setStage('loading');

      const name = common?.name.trim();
      const birthDate = common?.birthDate;
      const gender = common?.gender;

      if (name == null || birthDate == null || gender == null) {
        throw new Error();
      }

      const notNullVisas = visas.filter((x) => x != null) as IVisaInput[];

      const passportPost: IClientPassengerPostPutPassport | null =
        passport != null
          ? {
              issueDate: dateFormat(passport.issueDate, 'yyyy-mm-dd'),
              number: passport.number,
            }
          : null;

      const foreignPassportPost: IClientPassengerPostPutForeignPassport | null =
        foreignPassport != null
          ? {
              number: foreignPassport.number,
              dateIssue: dateFormat(foreignPassport.issueDate, 'yyyy-mm-dd'),
              dateExpire: dateFormat(foreignPassport.expirationDate, 'yyyy-mm-dd'),
            }
          : null;

      if (props.passengerId == null) {
        await ApiPassengers.getInstance().create(
          name,
          birthDate,
          gender,
          passportPost,
          foreignPassportPost,
          notNullVisas.map((v) => {
            return {
              visaId: v.visa.id,
              number: v.number,
              enterBefore: dateFormat(v.expirationDate, 'yyyy-mm-dd'),
              enterAfter: dateFormat(v.issueDate, 'yyyy-mm-dd'),
              durationOfStay: v.stayDaysCount,
            };
          })
        );
      } else {
        await ApiPassengers.getInstance().update(
          props.passengerId,
          name,
          birthDate,
          gender,
          passportPost,
          foreignPassportPost,
          notNullVisas.map((v) => {
            return {
              visaId: v.visa.id,
              number: v.number,
              enterBefore: dateFormat(v.expirationDate, 'yyyy-mm-dd'),
              enterAfter: dateFormat(v.issueDate, 'yyyy-mm-dd'),
              durationOfStay: v.stayDaysCount,
            };
          })
        );
      }

      props.onSaved();
      setStage('success');
    } catch {
      setStage('error');
    }
  };

  const isInputError = common == null || passport == null || foreignPassport == null || visas.length === 0;
  const isCriticalInputError = common == null || visas.findIndex((x) => x == null) >= 0;

  return (
    <Modal
      size='fullscreen'
      color='darker'
      header={props.passengerId == null ? 'Новый пассажир' : 'Изменение пассажира'}
      onClose={stage !== 'loading' ? props.onClose : undefined}
    >
      {getVisaModal && <ModalGetVisa onClose={() => setGetVisaModal(false)} />}
      {stage === 'loading' && (
        <Scroll padding={3}>
          <Box
            padding={20}
            className='new-passenger-modal-cnt-ctkM'
          >
            <Flex
              direction='column'
              gap={20}
            >
              <Header>Загрузка...</Header>
              <Message type='info'>Дождитесь завершения добавления пассажира в сервис</Message>
              <div />
              <Flex justifyContent='center'>
                <Loader
                  size='default'
                  color='blue'
                  variant='line'
                />
              </Flex>
              <div />
              <div />
            </Flex>
          </Box>
        </Scroll>
      )}
      {stage === 'success' && (
        <Scroll padding={3}>
          <Box
            padding={20}
            className='new-passenger-modal-cnt-ctkM'
          >
            <Flex
              direction='column'
              gap={20}
            >
              <Header>Пассажир сохранён</Header>
              <Message type='success'>Пассажир был успешно сохранён в сервисе и доступен к посадке на рейс</Message>
              <Button onClick={props.onClose}>Закрыть</Button>
            </Flex>
          </Box>
        </Scroll>
      )}
      {stage === 'error' && (
        <Scroll padding={3}>
          <Box
            padding={20}
            className='new-passenger-modal-cnt-ctkM'
          >
            <Flex
              direction='column'
              gap={20}
            >
              <Header>Ошибка</Header>
              <Message type='error'>
                Произошла ошибка при выполнении операции.
                <br />
                Возможно возникла внутренняя неполадка сервиса или был допущен неверный ввод данных.
              </Message>
              <Flex
                direction='column'
                gap={5}
              >
                <Button onClick={props.onClose}>Закрыть</Button>
              </Flex>
            </Flex>
          </Box>
        </Scroll>
      )}
      {stage === 'common' && (
        <Scroll padding={3}>
          <Flex
            direction='column'
            gap={20}
            className='new-passenger-modal-cnt-ctkM'
          >
            <CommonCard
              open={tab === 'common'}
              onOpen={() => handleTabOpen('common')}
              onChange={setCommon}
              initital={passenger}
            />
            <PassportCard
              open={tab === 'passport'}
              onOpen={() => handleTabOpen('passport')}
              onChange={setPassport}
              initial={passenger?.passport}
            />
            <ForeignPassportCard
              open={tab === 'foreign-passport'}
              onOpen={() => handleTabOpen('foreign-passport')}
              onChange={setForeignPassport}
              initial={passenger?.foreignPassport}
            />
            <VisaList
              open={tab === 'visa'}
              visaId={visaId}
              onOpen={(countryId) => handleTabOpen('visa', countryId)}
              onChanged={setVisas}
              onGetVisaClick={() => setGetVisaModal(!getVisaModal)}
              initial={passenger?.visas}
            />

            {isInputError && (
              <Message type={isCriticalInputError ? 'error' : 'warning'}>
                <Flex
                  direction='column'
                  gap={5}
                >
                  {common == null && <MessagePoint type='error'>Основные данные пассажира некорректны!</MessagePoint>}
                  {visas.findIndex((x) => x == null) >= 0 && (
                    <MessagePoint type='error'>
                      Одна или несколько виз добавлена, но некорректна! Либо заполните её до конца, либо удалите
                    </MessagePoint>
                  )}
                  {passport == null && <MessagePoint type='warning'>Данные паспорта некорректны</MessagePoint>}
                  {foreignPassport == null && (
                    <MessagePoint type='warning'>Данные заграничного паспорта некорректны</MessagePoint>
                  )}
                  {visas.length === 0 && <MessagePoint type='warning'>Не добавлено ни одной визы</MessagePoint>}
                </Flex>
              </Message>
            )}

            <Button
              disabled={isCriticalInputError}
              onClick={handleSaveClick}
              color='red'
            >
              {props.passengerId == null ? 'Добавить' : 'Сохранить'}
            </Button>
            <Button
              color='blue'
              onClick={props.onClose}
            >
              Отменить
            </Button>
          </Flex>
        </Scroll>
      )}
    </Modal>
  );
}

export default ModalAddPassenger;
